import React, { useContext, useCallback } from "react";
import "./M_LateralMenu.css";
import { NavigateContext, LoadingContext } from "src/M_Components";
import { useTranslation } from "react-i18next";

import {
  Box,
  ListItemText,
  ListItemButton,
  ListItem,
  IconButton,
  Tooltip,
} from "@mui/material";

import KeyboardArrowDown from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUp from "@mui/icons-material/KeyboardArrowUp";
import TripOriginIcon from "@mui/icons-material/TripOrigin";
import ListItemIcon from "@mui/material/ListItemIcon";

import { DynamicMuiIcon } from "src/M_Components/M_Utils/DynamicMuiIcon";
import PropTypes from "prop-types";
import UserSettingsMenu from "src/components/settings/UserSettingsMenu";

/* =============================================================================
   MLateralMenu
   - Layout only, sizes & behavior are handled by CSS
   - Fixed to viewport (below top navbar) and scrolls internally
   - Supports ellipsis + marquee on long labels
   - Uses CSS custom properties for theming (secondary/tertiary colors)
   ========================================================================== */
export function MLateralMenu({
  showMenu,
  setShowMenu,
  menus,
  setMenus,
  bodyPage,
  handleChangeLng,
  handleLogout,
  handleSettings,
  handleMenuPages,
}) {
  const ctx = useContext(NavigateContext);
  const ctx2 = useContext(LoadingContext);
  const { t } = useTranslation();

  // Theming colors (from LoadingContext, with fallbacks)
  const secondaryColor = ctx2.secondaryColor || "var(--color-grey-dark)";
  const tertiaryColor = ctx2.tertiaryColor || "rgba(0, 0, 0, 0.04)";

  /* ---------------------------------------------------------------------------
     Helpers to control Menu / Submenus state
     ------------------------------------------------------------------------ */

  const updateMenu = useCallback(
    (menu, SubMenus) => {
      const newMenu = menus.map((elem) => {
        if (elem.MenuLabel === menu.MenuLabel) {
          return {
            ...elem,
            SubMenus: elem.SubMenus.map((elem2) => ({
              ...elem2,
              MenuOpen:
                elem2.MenuLabel === SubMenus?.MenuLabel
                  ? !elem2.MenuOpen
                  : false,
            })),
          };
        }
        return elem;
      });

      setMenus(newMenu);
    },
    [menus, setMenus]
  );

  const changeMenu = useCallback(
    (menu) => {
      const newMenu = menus.map((elem) => {
        if (menu?.MenuLabel === elem.MenuLabel) {
          const MenuOpen = !elem.MenuOpen;
          return {
            ...elem,
            MenuOpen,
            SubMenus: MenuOpen
              ? elem.SubMenus.map((sub) => ({ ...sub, MenuOpen: false }))
              : elem.SubMenus,
          };
        }
        return { ...elem, MenuOpen: false };
      });

      setMenus(newMenu);
    },
    [menus, setMenus]
  );

  /* ---------------------------------------------------------------------------
     Handlers
     ------------------------------------------------------------------------ */

  const onClickMenu = (event, menu) => {
    if (!menu?.SubMenus || menu?.SubMenus?.length === 0) {
      setShowMenu(false);
      handleMenuPages(menu.MenuLabelUrl, t(menu.MenuDisplay), event);
    } else {
      changeMenu(menu);
    }
  };

  const onClickSubMenu = (event, menu, SubMenus) => {
    if (SubMenus?.SubMenus?.length === 0) {
      setShowMenu(false);
      handleMenuPages(SubMenus?.MenuLabelUrl, t(SubMenus?.MenuDisplay), event);
    } else {
      const newSubMenu = menus.map((elem) => {
        if (elem.MenuLabel === menu.MenuLabel) {
          return {
            ...elem,
            SubMenus: elem.SubMenus.map((elem2) => ({
              ...elem2,
              MenuOpen:
                elem2.MenuLabel === SubMenus.MenuLabel
                  ? !elem2.MenuOpen
                  : false,
            })),
          };
        }
        return elem;
      });

      setMenus(newSubMenu);
    }
  };

  /* ---------------------------------------------------------------------------
     Sub menu (level 2)
     - Keeps original border-left idea and color behavior
     ------------------------------------------------------------------------ */
  const subMenuListItem = (menu, SubMenus) => (
    <ListItemButton
      disableRipple
      key={SubMenus?.MenuLabel}
      className={`menuSubItem ${SubMenus?.MenuOpen ? "open" : ""}`}
      onClick={(event) => onClickSubMenu(event, menu, SubMenus)}
      onMouseDown={(event) => {
        if (event.button === 1) {
          // Middle-click opens in new tab (same behavior as before)
          if (SubMenus?.SubMenus?.length === 0) {
            document.body.onmousedown = (e) => {
              if (e.button === 1) return false;
            };
            ctx.newTab(
              `${SubMenus?.MenuLabelUrl}`,
              `${t(SubMenus?.MenuDisplay)}`,
              event
            );
          } else {
            updateMenu(menu, SubMenus);
          }
        }
      }}
    >
      <ListItemText
        primary={
          <span className="menuLabelContainer">
            <span className="menuLabelText">{t(SubMenus?.MenuDisplay)}</span>
          </span>
        }
      />
    </ListItemButton>
  );

  /* ---------------------------------------------------------------------------
     Sub Sub Menu (level 3)
     ------------------------------------------------------------------------ */
  const subSubMenuListItem = (subSubMenu) => (
    <ListItemButton
      key={subSubMenu?.MenuLabel}
      className="menuSubSubItem"
      onClick={(event) => {
        setShowMenu(false);
        ctx.newTab(
          `${subSubMenu.MenuLabelUrl}`,
          `${t(subSubMenu.MenuDisplay)}`,
          event
        );
      }}
      onMouseDown={(event) => {
        if (event.button === 1) {
          document.body.onmousedown = (e) => {
            if (e.button === 1) return false;
          };
          ctx.newTab(
            `${subSubMenu.MenuLabelUrl}`,
            `${t(subSubMenu.MenuDisplay)}`,
            event
          );
        }
      }}
    >
      <ListItemIcon className="subSubMenuIcon">
        <TripOriginIcon />
      </ListItemIcon>

      <ListItemText
        primary={
          <span className="menuLabelContainer">
            <span className="menuLabelText">{t(subSubMenu.MenuLabel)}</span>
          </span>
        }
      />
    </ListItemButton>
  );

  /* ---------------------------------------------------------------------------
     Render
     - Passes CSS custom properties for secondary / tertiary colors
     ------------------------------------------------------------------------ */
  return (
    <div className="homepageTemplate">
      <div
        className={`homePageMenuTemplate ${showMenu ? "open" : "closed"}`}
        style={{
          "--menu-secondary": secondaryColor,
          "--menu-tertiary": tertiaryColor,
        }}
      >
        <div className="principalMenu">
          <div className="menuScrollArea">
            <div className="principalMenuTitle">
              <span className="principalMenuTitleText">{t("Menu")}</span>
            </div>

            {menus?.map((menu, key) => (
              <div className="menuBlock" key={menu?.MenuLabel || key}>
                <Box
                  className={`menuItemWrapper ${menu.MenuOpen ? "open" : ""}`}
                >
                  <Tooltip title={t(menu.MenuDisplay)} placement="right">
                    <ListItem component="div" disablePadding>
                      <ListItemButton
                        disableRipple
                        className="mainMenuItem"
                        onClick={(event) => onClickMenu(event, menu)}
                        onMouseDown={(event) => {
                          if (event.button === 1) {
                            document.body.onmousedown = (e) => {
                              if (e.button === 1) return false;
                            };
                            if (
                              !menu?.SubMenus ||
                              menu?.SubMenus?.length === 0
                            ) {
                              ctx.newTab(
                                `${menu?.MenuLabelUrl}`,
                                `${t(menu?.MenuDisplay)}`,
                                event
                              );
                            } else {
                              changeMenu(menu);
                            }
                          }
                        }}
                      >
                        {/* Icon color is now fully controlled by CSS classes */}
                        <DynamicMuiIcon
                          iconName={menu.MenuIcon}
                          className={`menuIcon ${
                            menu.MenuOpen ? "menuIcon-open" : ""
                          }`}
                          sx={{
                            // Keep only the filter behavior from the old implementation
                            filter: menu.MenuOpen
                              ? menu.MenuIconFilter
                              : "invert(35%) sepia(0%) saturate(34%) hue-rotate(170deg) brightness(84%) contrast(91%)",
                          }}
                        />

                        <ListItemText
                          primary={
                            <span className="menuLabelContainer">
                              <span className="menuLabelText">
                                {t(menu?.MenuDisplay)}
                              </span>
                            </span>
                          }
                        />

                        {menu?.SubMenus && menu?.SubMenus?.length > 0 && (
                          <IconButton size="large" disableRipple>
                            {menu?.MenuOpen ? (
                              <KeyboardArrowUp
                                style={{ color: secondaryColor }}
                              />
                            ) : (
                              <KeyboardArrowDown
                                style={{ color: "var(--color-grey-dark)" }}
                              />
                            )}
                          </IconButton>
                        )}
                      </ListItemButton>
                    </ListItem>
                  </Tooltip>

                  {menu?.MenuOpen &&
                    menu?.SubMenus.map((SubMenus, key) => (
                      <div key={SubMenus?.MenuLabel || key}>
                        <Tooltip
                          title={t(SubMenus?.MenuDisplay)}
                          placement="right"
                        >
                          {subMenuListItem(menu, SubMenus)}
                        </Tooltip>

                        {SubMenus?.MenuOpen &&
                          SubMenus?.SubMenus.map((subSubMenu, key) => (
                            <div key={subSubMenu?.MenuLabel || key}>
                              <Tooltip
                                title={t(subSubMenu.MenuLabel)}
                                placement="right"
                              >
                                {subSubMenuListItem(subSubMenu)}
                              </Tooltip>
                            </div>
                          ))}
                      </div>
                    ))}
                </Box>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom user menu */}
        <div className="userSettingsMenu">
          <UserSettingsMenu
            handleSettings={handleSettings}
            handleLogout={handleLogout}
            handleChangeLng={handleChangeLng}
          />
        </div>
      </div>

      {/* Main body (landing / pages) */}
      {bodyPage}
    </div>
  );
}

MLateralMenu.propTypes = {
  showMenu: PropTypes.bool,
  setShowMenu: PropTypes.func,
  menus: PropTypes.array,
  setMenus: PropTypes.func,
  bodyPage: PropTypes.node,
  handleChangeLng: PropTypes.func,
  handleLogout: PropTypes.func,
  handleSettings: PropTypes.func,
};
